import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import ActionsButton from '../buttons/ActionsButton';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { getHistoricalStream, getLiveStream, getStreamTimes } from '../../utils/video';
import { getMothershipLogs } from '../../utils/api';
import type { Location } from '../../types/Location';
import type { Franchise } from '../../types/Franchise';
import { useCamera } from '../../hooks/useCamera';

export default function MetricsDisplayer({
  location,
  camera,
  metrics = {},
  franchise,
  showMetrics,
}: {
  location: Location;
  camera: string;
  metrics?: Record<string, any>;
  franchise?: Franchise | undefined;
  showMetrics: Record<string, boolean>;
}): JSX.Element {
  const { kinesisVideo, isLive, playbackSpeed, videoDateTime, setVideoDateTime, isPlaying, setIsPlaying } = useCamera();
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [player, setPlayer] = useState<videojs.Player | null>(null);
  const [showAllCamerasOut, setShowAllCamerasOut] = useState(false);
  const [showAllNmapResults, setShowAllNmapResults] = useState(false);
  const [logs, setLogs] = useState<string>('');
  const [logsOpen, setLogsOpen] = useState(false);
  const locationPath = useLocation();

  useEffect(() => {
    if (!videoRef.current) return;
    const playerObject = videojs(videoRef.current, {
      autoplay: true,
      muted: true,
      playsinline: true,
      controls: true,
      preload: 'auto',
      userActions: { click: false },
      html5: { vhs: { overrideNative: true } },
      disablePictureInPicture: true,
      nativeControlsForTouch: false,
    });
    setPlayer(playerObject);

    playerObject.on('error', () => {
      playerObject.load();
      playerObject.play()?.catch((err) => console.log(err));
    });

    playerObject.on('loadedmetadata', () => {
      const w = wrapperRef.current?.clientWidth;
      playerObject.width(w);
    });

    if (isLive) {
      goToLive().catch((err) => console.log(err));
    } else {
      goToHistorical().catch((err) => console.log(err));
    }

    setIsPlaying(true);

    return () => {
      if (player) player.dispose();
    };
  }, [videoRef.current]);

  useEffect(() => {
    if (!player) return;
    if (!isPlaying) {
      player.pause();
    } else {
      player.play()?.catch((err) => console.log(err));
    }
  }, [isPlaying, player]);

  useEffect(() => {
    if (!player) return;
    try {
      player.playbackRate(playbackSpeed);
      player.defaultPlaybackRate(playbackSpeed);
    } catch (err) {
      console.log(err);
    }
  }, [playbackSpeed, player]);

  useEffect(() => {
    if (!player) return;
    if (isLive) {
      goToLive().catch((err) => console.log(err));
    } else {
      goToHistorical().catch((err) => console.log(err));
    }
  }, [player, isLive, videoDateTime, location, camera]);

  const getStreamName = (): string => {
    if (camera === 'CV') return `CV_${location.id}`;
    if (camera === 'CV_DT') return `CV_DT_${location.id}`;
    if (camera === 'MONITOR') return `MONITOR_${location.id}`;
    return `${location.id}_${camera}`;
  };

  const goToLive = async (): Promise<void> => {
    if (!player || !kinesisVideo) return;
    try {
      const streamUrl = await getLiveStream(getStreamName(), kinesisVideo);
      player.src({ src: streamUrl, type: 'application/x-mpegURL' });
      player.playbackRate(1);
      player.defaultPlaybackRate(1);
    } catch (err) {
      console.warn('Error going to live stream:', err);
    }
  };

  const goToHistorical = async (): Promise<void> => {
    if (!player || !kinesisVideo) return;
    try {
      player.on('ended', () => {
        setVideoDateTime(new Date(videoDateTime.valueOf() + 10 * 60 * 1000));
      });
      const { startTime, endTime } = getStreamTimes(videoDateTime, location.timezone);
      const streamUrl = await getHistoricalStream(getStreamName(), kinesisVideo, startTime, endTime);
      player.src({ src: streamUrl, type: 'application/x-mpegURL' });
    } catch (err) {
      console.warn('Error going to historical stream:', err);
    }
  };

  const renderCircle = (value: boolean) => (
    <span
      style={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: value ? 'green' : 'red',
        display: 'inline-block',
        marginLeft: 5,
      }}
    ></span>
  );

  const renderRamUsageCircle = (ramUsage: number) => {
    const color = ramUsage > 90 ? 'red' : ramUsage > 70 ? 'yellow' : 'green';
    return (
      <span
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: color,
          display: 'inline-block',
          marginLeft: 5,
        }}
      ></span>
    );
  };

  const renderDiskUsageCircle = (diskUsage: number) => {
    const color = diskUsage >= 70 ? 'red' : diskUsage > 45 ? 'orange' : 'green';
    return (
      <span
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: color,
          display: 'inline-block',
          marginLeft: 5,
        }}
      ></span>
    );
  };

  const renderUploadSpeedCircle = (uploadSpeed: number) => {
    const color = uploadSpeed > 20 ? 'green' : uploadSpeed >= 10 ? 'yellow' : 'red';
    return (
      <span
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: color,
          display: 'inline-block',
          marginLeft: 5,
        }}
      ></span>
    );
  };

  const renderRestartCVCircle = (restartCv: string) => {
    try {
      const dates = restartCv.split(',').map((date) => new Date(date.trim()));
      const pastWeekDates = dates.filter((date) => {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        return date >= oneWeekAgo;
      });
      const count = pastWeekDates.length;
      const circles = [];

      if (count === 0) {
        circles.push(
          <span
            key="green"
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: 'green',
              display: 'inline-block',
              marginLeft: 5,
            }}
          ></span>
        );
      } else if (count === 1) {
        circles.push(
          <span
            key="yellow"
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: 'yellow',
              display: 'inline-block',
              marginLeft: 5,
            }}
          ></span>
        );
      } else {
        for (let i = 0; i < count; i++) {
          circles.push(
            <span
              key={`red-${i}`}
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: 'red',
                display: 'inline-block',
                marginLeft: 5,
              }}
            ></span>
          );
        }
      }
      return <>{circles}</>;
    } catch (error) {
      console.error('Error rendering Restart CV Circle:', error);
      return <></>;
    }
  };

  const renderRestartAllCircle = (restartAll: string) => {
    try {
      const dates = restartAll.split(',').map((date) => new Date(date.trim()));
      const pastMonthDates = dates.filter((date) => {
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        return date >= oneMonthAgo;
      });
      const pastTwoWeeksDates = dates.filter((date) => {
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        return date >= twoWeeksAgo;
      });
      const pastWeekDates = dates.filter((date) => {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        return date >= oneWeekAgo;
      });

      const circles = [];
      if (pastWeekDates.length > 0) {
        for (let i = 0; i < pastWeekDates.length; i++) {
          circles.push(
            <span
              key={`red-${i}`}
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: 'red',
                display: 'inline-block',
                marginLeft: 5,
              }}
            ></span>
          );
        }
      } else if (pastTwoWeeksDates.length > 0) {
        circles.push(
          <span
            key="red"
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: 'red',
              display: 'inline-block',
              marginLeft: 5,
            }}
          ></span>
        );
      } else if (pastMonthDates.length > 0) {
        circles.push(
          <span
            key="yellow"
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: 'yellow',
              display: 'inline-block',
              marginLeft: 5,
            }}
          ></span>
        );
      } else {
        circles.push(
          <span
            key="green"
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: 'green',
              display: 'inline-block',
              marginLeft: 5,
            }}
          ></span>
        );
      }
      return <>{circles}</>;
    } catch (error) {
      console.error('Error rendering Restart All Circle:', error);
      return <></>;
    }
  };

  const getCollectionTimestampColor = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const utcNow = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
      const differenceInHours = (utcNow.getTime() - date.getTime()) / (1000 * 60 * 60);
      return differenceInHours > 2 ? 'red' : differenceInHours > 1 ? 'yellow' : 'green';
    } catch (error) {
      console.error('Error calculating Collection Timestamp color:', error);
      return 'green';
    }
  };

  const renderCollectionTimestampCircle = (timestamp: string): JSX.Element => (
    <span
      style={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: getCollectionTimestampColor(timestamp),
        display: 'inline-block',
        marginLeft: 5,
      }}
    ></span>
  );

  const compareCameras = (
    camerasOutContents: string,
    nmapResults: string,
    showAllCamerasOut: boolean,
    setShowAllCamerasOut: React.Dispatch<React.SetStateAction<boolean>>,
    showAllNmapResults: boolean,
    setShowAllNmapResults: React.Dispatch<React.SetStateAction<boolean>>
  ): JSX.Element => {
    try {
      const camerasOutList = camerasOutContents.split('\n').map((ip) => ip.trim());
      const nmapResultList = nmapResults.split(',').map((ip) => ip.trim());
      const onlyInCamerasOut = camerasOutList.filter((ip) => !nmapResultList.includes(ip));
      const onlyInNmapResults = nmapResultList.filter((ip) => !camerasOutList.includes(ip));
      const hasDiscrepancy = onlyInCamerasOut.length > 0 || onlyInNmapResults.length > 0;

      return (
        <div style={{ marginTop: '10px' }}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: hasDiscrepancy ? 'red' : 'green',
              display: 'inline-block',
              marginRight: 5,
            }}
          ></span>
          <span>{hasDiscrepancy ? 'Discrepancy found!' : 'No discrepancies'}</span>
          <div style={{ marginTop: '10px' }}>
            <strong
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => setShowAllCamerasOut(!showAllCamerasOut)}
            >
              Cameras Out Contents ({camerasOutList.length})
            </strong>
            <pre>
              {(showAllCamerasOut ? camerasOutList : onlyInCamerasOut).map((ip) => (
                <span key={`camera-${ip}`} style={{ color: onlyInCamerasOut.includes(ip) ? 'red' : 'white' }}>
                  {ip}
                  {'\n'}
                </span>
              ))}
            </pre>
          </div>
          <div style={{ marginTop: '10px' }}>
            <strong
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => setShowAllNmapResults(!showAllNmapResults)}
            >
              Nmap Results ({nmapResultList.length})
            </strong>
            <pre>
              {(showAllNmapResults ? nmapResultList : onlyInNmapResults).map((ip) => (
                <span key={`nmap-${ip}`} style={{ color: onlyInNmapResults.includes(ip) ? 'red' : 'white' }}>
                  {ip}
                  {'\n'}
                </span>
              ))}
            </pre>
          </div>
        </div>
      );
    } catch (error) {
      console.error('Error comparing cameras:', error);
      return <span>Error comparing cameras.</span>;
    }
  };

  const handleLogsOpen = async () => {
    try {
      const fetchedLogs = await getMothershipLogs(franchise!.id, [location.id]);
      setLogs(fetchedLogs || 'No logs found.');
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLogs('Failed to load logs.');
    } finally {
      setLogsOpen(true);
    }
  };

  const handleLogsClose = () => setLogsOpen(false);

  const renderMetric = (key: string, value: any, renderFn?: (val: any) => JSX.Element) => {
    if (!showMetrics[key]) return null;
    return (
      <div key={key}>
        {renderFn ? renderFn(value) : null}
        <span>{`${key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').toUpperCase()}: ${value || 'N/A'}`}</span>
      </div>
    );
  };

  return (
    <div>
      {camera === 'MONITOR' && locationPath.pathname === '/mothership' && (
        <div>
          <ActionsButton locationId={location.id} onMenuClose={() => {}} />
          <Button variant="contained" color="secondary" onClick={handleLogsOpen} style={{ marginLeft: '10px' }}>
            View Logs
          </Button>
          <Dialog open={logsOpen} onClose={handleLogsClose} fullWidth maxWidth="md">
            <DialogTitle>Mothership Logs</DialogTitle>
            <DialogContent>
              <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                {logs}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleLogsClose} color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>
          {Object.keys(metrics).length > 0 && (
            <div
              style={{
                marginTop: 10,
                backgroundColor: getCollectionTimestampColor(metrics.timestamp) === 'red' ? 'rgba(255, 0, 0, 0.2)' : 'transparent',
                padding: '10px',
                borderRadius: '8px',
              }}
            >
              {renderMetric('internetConnection', metrics.internet_connection, (val) => renderCircle(val))}
              {renderMetric('ramUsage', metrics.ram_usage, (val) => renderRamUsageCircle(val))}
              {renderMetric('diskUsage', metrics.disk_usage, (val) => renderDiskUsageCircle(val))}
              {renderMetric('uploadSpeed', metrics.speedtest_upload, (val) => renderUploadSpeedCircle(val))}
              {renderMetric('restartCv', metrics.restart_cv, (val) => renderRestartCVCircle(val))}
              {renderMetric('restartAll', metrics.restart_all, (val) => renderRestartAllCircle(val))}
              {renderMetric('cameras', null, () =>
                metrics.cameras_out_contents && metrics.nmap_result
                  ? compareCameras(
                      metrics.cameras_out_contents,
                      metrics.nmap_result,
                      showAllCamerasOut,
                      setShowAllCamerasOut,
                      showAllNmapResults,
                      setShowAllNmapResults
                    )
                  : null
              )}
              {renderMetric('collectionTimestamp', metrics.timestamp, (val) => renderCollectionTimestampCircle(val))}
              {renderMetric('sudoCrontab', metrics.sudo_crontab)}
              {renderMetric('envContents', metrics.env_contents)}
              {renderMetric('freeSwap', metrics.free_swap)}
              {renderMetric('internalIp', metrics.internal_ip)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}